<script lang="ts">
  /**
   * Letter - Floating letter entity for Monster Mode
   * Player collects these to spell words
   */

  import { scale, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';

  // Props
  interface Props {
    letter: string;
    x: number;
    y: number;
    collected?: boolean;
    debug?: boolean;
  }

  let { letter, x, y, collected = false, debug = false }: Props = $props();

  // Get color based on letter (6 color variations)
  function getLetterColor(letter: string): string {
    const colors = [
      '#EF4444', // Red
      '#F97316', // Orange
      '#FBBF24', // Yellow
      '#22C55E', // Green
      '#3B82F6', // Blue
      '#A855F7'  // Purple
    ];
    const index = letter.charCodeAt(0) % colors.length;
    return colors[index];
  }

  const color = getLetterColor(letter);
</script>

{#if !collected}
  <div
    class="letter"
    style="left: {x}px; top: {y}px; --letter-color: {color};"
    transition:scale={{ duration: 300, easing: cubicOut }}
  >
    <div class="letter-content">
      {letter.toUpperCase()}
    </div>

    <!-- Debug collision boundary -->
    {#if debug}
      <div class="debug-boundary" title="Letter collision radius: 35px"></div>
    {/if}
  </div>
{/if}

<style>
  .letter {
    position: absolute;
    width: 70px;
    height: 70px;
    transform: translate(-50%, -50%);
    z-index: 40;
    pointer-events: none;
    animation: float 3s ease-in-out infinite;
  }

  .letter-content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--letter-color);
    border-radius: 50%;
    border: 3px solid white;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 0 20px rgba(255, 255, 255, 0.3);
    font-size: 36px;
    font-weight: bold;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  /* Floating animation */
  @keyframes float {
    0%, 100% {
      transform: translate(-50%, -50%) translateY(0) rotate(0deg);
    }
    25% {
      transform: translate(-50%, -50%) translateY(-10px) rotate(2deg);
    }
    50% {
      transform: translate(-50%, -50%) translateY(0) rotate(0deg);
    }
    75% {
      transform: translate(-50%, -50%) translateY(-5px) rotate(-2deg);
    }
  }

  /* Debug collision boundary */
  .debug-boundary {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 70px; /* Letter radius = 35px, so diameter = 70px */
    height: 70px;
    transform: translate(-50%, -50%);
    border: 2px solid cyan;
    border-radius: 50%;
    background: rgba(0, 255, 255, 0.1);
    pointer-events: none;
    z-index: 100;
  }

  /* Responsive */
  @media (max-width: 767px) {
    .letter {
      width: 60px;
      height: 60px;
    }

    .letter-content {
      font-size: 30px;
      border-width: 2px;
    }
  }
</style>
